import { Application, Text } from 'pixi.js';
import { TitleScreen } from '@game/world/TitleScreen';
import { Introduction } from '@game/world/Introduction';

/**
 * Little Garden Readers - Main Entry Point
 *
 * This is an audio-first, touch-first learning experience
 * for young children (approximately age 4).
 *
 * Core principles:
 * - The world IS the interface (no menus)
 * - Audio clarity matters more than visuals
 * - Observations, not tracking
 * - Never assess or grade the child
 */

async function init() {
  const container = document.getElementById('game-container');

  if (!container) {
    console.error('Game container not found');
    return;
  }

  // Create PixiJS application
  const app = new Application();

  await app.init({
    resizeTo: window,
    background: '#F5F5DC', // Beige garden color
    antialias: true,
    resolution: window.devicePixelRatio || 2, // Support retina displays
    autoDensity: true,
  });

  container.appendChild(app.canvas);

  // Go straight to game - audio will unlock on first interaction
  startGame(app);
}

/**
 * Start the main game - shows loading screen first
 */
async function startGame(app: Application): Promise<void> {
  // Show loading screen
  const loadingText = new Text({
    text: '🌱\n\nLoading Little Garden...',
    style: {
      fontFamily: 'system-ui, sans-serif',
      fontSize: 48,
      fill: '#4A7C59',
      align: 'center',
    }
  });

  loadingText.anchor.set(0.5);
  loadingText.x = app.screen.width / 2;
  loadingText.y = app.screen.height / 2;
  app.stage.addChild(loadingText);

  // Brief loading animation
  await new Promise(resolve => setTimeout(resolve, 500));

  // Remove loading screen
  app.stage.removeChild(loadingText);

  // Create title screen with transition callback
  const titleScreen = new TitleScreen(app, async () => {
    // Animate transition out (fade + Flutter flight to left)
    const flutterPosition = await titleScreen.transitionOut();

    // Hide title screen immediately (Flutter disappears)
    titleScreen.hide();

    // Enter introduction (Flutter picks up at exact same position)
    await enterGarden(app, flutterPosition);
  });

  try {
    // Load and show title screen
    await titleScreen.load();
    await titleScreen.show();
    console.log('✓ Title screen ready');
  } catch (err) {
    console.error('Failed to load title screen:', err);

    // Fallback: show error
    const errorText = new Text({
      text: 'Failed to load garden\n\nCheck console for errors',
      style: {
        fontFamily: 'system-ui, sans-serif',
        fontSize: 32,
        fill: '#D55C5C',
        align: 'center',
      }
    });
    errorText.anchor.set(0.5);
    errorText.x = app.screen.width / 2;
    errorText.y = app.screen.height / 2;
    app.stage.addChild(errorText);
  }

  // TODO: Initialize observation system (IndexedDB)
}

/**
 * Enter the introduction/tutorial scene
 */
async function enterGarden(app: Application, flutterPosition?: { x: number; y: number }): Promise<void> {
  console.log('🌱 Loading introduction...');

  const introduction = new Introduction(app, flutterPosition);

  try {
    await introduction.load();
    await introduction.show();
    console.log('✓ Introduction scene ready - Flutter will guide');
  } catch (err) {
    console.error('Failed to load introduction:', err);
  }
}


// Start the game
init().catch(err => {
  console.error('Failed to initialize game:', err);
});
